import { useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher } from '@remix-run/react';
import { atom, useAtom } from 'jotai';
import type { BuildHubViewFragment, PageInfo } from 'generated/graphql';
import {
    BUILDHUB_PROJECTS_API,
    type loader,
} from '~/routes/remix-api.build-hub.projects';
import { useUserLoggedIn } from '~/modules/user';

/**
 * The cached BuildHub projects and pagination information for the current
 * user with no filters applied.
 */
const buildHubProjectsCacheAtom = atom<{
    projects: BuildHubViewFragment[];
    pageInfo?: PageInfo;
    offset: number;
} | null>(null);

/**
 * Hook to get the BuildHub projects for the current user.
 * @param limit The maximum number of projects to return. Do not change this
 *  value. Defaults to 50.
 */
export function useBuildHubProjects({
    limit = 50,
}: {
    limit?: number;
} = {}) {
    const { isLoggedIn } = useUserLoggedIn();
    const [cache, setCache] = useAtom(buildHubProjectsCacheAtom);
    const isFetched = useRef(false);
    const [offset] = useState(0);
    const [projects, setProjects] = useState<BuildHubViewFragment[]>(
        cache?.projects ?? []
    );
    const [pageInfo, setPageInfo] = useState<PageInfo | undefined>(
        cache?.pageInfo
    );
    const fetcher = useFetcher<typeof loader>();
    const apiUrl = useMemo(() => {
        const url = new URL(BUILDHUB_PROJECTS_API, window.location.origin);
        url.searchParams.set('limit', String(limit));
        url.searchParams.set('offset', String(offset));
        return `${url.pathname}${url.search}`;
    }, [limit, offset]);

    useEffect(() => {
        // Fetch initial results on mount.
        if (!fetcher.data && fetcher.state === 'idle' && !isFetched.current) {
            fetcher.load(apiUrl);
            isFetched.current = true;
        }
    }, [fetcher, apiUrl]);

    // Set data from fetcher and cache results.
    useEffect(() => {
        if (!fetcher.data || fetcher.state !== 'idle') {
            return;
        }
        const newProjects = fetcher.data.nodes;
        const newPageInfo = fetcher.data.pageInfo;
        setProjects(newProjects);
        setPageInfo(newPageInfo);
        setCache({ projects: newProjects, pageInfo: newPageInfo, offset });
    }, [offset, fetcher, setCache]);

    // Clear the build hub projects cache when the user logs out.
    useEffect(() => {
        if (!isLoggedIn) {
            setCache({ projects: [], offset: 0 });
            isFetched.current = false;
        }
    }, [isLoggedIn, setCache]);

    return {
        /**
         * The list of BuildHub projects.
         */
        projects,
        /**
         * There are more contact suggestions to load.
         */
        hasMore: pageInfo?.hasNextPage ?? false,
        /**
         * The suggestions are currently being loaded.
         */
        isLoading: fetcher.state !== 'idle',
    };
}
